import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import ReviewsSection from "../components/ReviewsSection";
import PhotoPlaceholder from "../components/PhotoPlaceholder";
import OperatorAvatar from "../components/OperatorAvatar";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useParams, Link } from "react-router-dom";
import { locationService } from "../services/locationListingService";
import type { Location } from "../types/location";
import TemperatureInput from "../components/TemperatureInput";
import { 
  MapPin, 
  Star,
  Clock,
  Users,
  Wifi,
  Lock,
  Accessibility,
  ThermometerSun,
  Leaf,
  Heart,
  ArrowLeft,
  CheckCircle,
  Award,
  Shield,
  Phone,
  Mail,
  Globe,
  Instagram,
  Facebook,
  Twitter,
  Share2,
  Upload,
  Clipboard,
  Flame,
  Droplet,
  Coffee,
  Car,
  ArrowRight,
  Sparkles,
  Key,
  MessageSquare,
  Tag,
  Calendar,
  ChevronDown,
  User,
  AlertCircle,
  Map,
  X
} from "lucide-react";
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";
import { useAuth } from "../hooks/useAuth";

const SaunaDetailsNordic: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [location, setLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [isFavourited, setIsFavourited] = useState(false);
  const { user } = useAuth();

  // Helper function to safely render country data
  const renderCountry = (country: any): string => {
    if (typeof country === 'string') {
      return country;
    } else if (country && typeof country === 'object') {
      return country.name || country.code || 'Ireland';
    } else {
      return 'Ireland';
    }
  };

  // Use the ID from URL params, fallback to hardcoded ID if not provided
  const locationId = id || "14";

  useEffect(() => {
    const fetchLocation = async () => {
      setLoading(true);
      try {
        const data = await locationService.getLocation(locationId);
        setLocation(data);
      } catch (err) {
        console.error("API Error:", err);
        // Use mock data as fallback when API fails
        const mockLocation: Location = {
          id: "nordic-wellness",
          name: "SaunaDip Wild Retreat – Atlantic View Sauna & Cold Plunge",
          description: "Escape to the Irish wilds with a restorative wood-fired sauna and invigorating cold plunge. Nestled by the Atlantic with sea views and birdsong, SaunaDip offers a soulful retreat from modern noise. Our sauna comfortably seats 6 guests with natural spring water for the plunge pool. Accessible via a gravel path with on-site shower facilities. Heat up. Cool down. Reconnect.",
          address: "Knockroe, County Clare, Ireland",
          city: "County Clare",
          county: "Clare",
          country: "Ireland",
          latitude: 52.8,
          longitude: -9.5,
          type: "sauna",
          serviceTypes: ["sauna", "cold_plunge"],
          placeTypes: ["wellness_center"],
          experiences: ["traditional_finnish"],
          amenities: [
            "Wood-fired sauna",
            "Cold plunge pool",
            "Outdoor seating & fire pit",
            "Changing area",
            "Free herbal tea",
            "Heated towels",
            "Private parking",
            "Atlantic sea views"
          ],
          images: [
            "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1200&h=600&fit=crop",
            "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=400&h=300&fit=crop",
            "https://images.unsplash.com/photo-1544161512-6ad2f9d19b9a?w=400&h=300&fit=crop",
            "https://images.unsplash.com/photo-1544161512-6ad2f9d19b9a?w=400&h=300&fit=crop",
            "https://images.unsplash.com/photo-1544161512-6ad2f9d19b9a?w=400&h=300&fit=crop"
          ],
          services: [
            "Traditional Finnish sauna",
            "Cold plunge therapy",
            "Guided sessions available",
            "Private bookings",
            "Wellness consultations"
          ],
          operator: {
            id: "saunadip-team",
            name: "SaunaDip Team",
            phone: "+353123456789",
            website: "https://saunadip.com",
            email: "hello@saunadip.com",
            createdAt: "2024-01-01T00:00:00Z",
            updatedAt: "2024-01-01T00:00:00Z"
          },
          rating: 4.8,
          price: "€20",
          createdAt: "2024-01-01T00:00:00Z",
          updatedAt: "2024-01-01T00:00:00Z"
        };
        setLocation(mockLocation);
        setError(null);
      } finally {
        setLoading(false);
      }
    };
    fetchLocation();
  }, [locationId]);

  const renderStars = (rating: number) => {
    return (
      <div className="flex">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`h-4 w-4 ${
              i < rating ? 'text-sauna-orange fill-sauna-orange' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner size="lg" variant="default" />
      </div>
    );
  }

  if (error || !location) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-sauna-navy mb-4">Something went wrong</h2>
          <p className="text-gray-600 mb-6">Unable to load sauna details. Please try again later.</p>
          <Button onClick={() => window.history.back()}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Sample reviews data
  const sampleReviews = [
    {
      id: "1",
      user: {
        name: "Sarah M.",
        avatar: "",
        memberSince: "2024",
        location: "Dublin"
      },
      rating: 5,
      comment: "Absolutely magical experience! The wood-fired sauna was perfectly hot and the cold plunge was invigorating. The Atlantic views are breathtaking. Will definitely return!",
      date: "2024-12-15",
      categories: {
        cleanliness: 5,
        heat_quality: 5,
        atmosphere: 5,
        arrival_experience: 5,
        privacy_comfort: 4,
        communication: 5,
        value: 5
      }
    },
    {
      id: "2",
      user: {
        name: "Michael K.",
        avatar: "",
        memberSince: "2023",
        location: "Galway"
      },
      rating: 4,
      comment: "Great sauna experience with stunning ocean views. The facilities were clean and well-maintained. The only minor issue was finding the entrance initially, but once there, everything was perfect.",
      date: "2024-12-10",
      categories: {
        cleanliness: 5,
        heat_quality: 4,
        atmosphere: 5,
        arrival_experience: 3,
        privacy_comfort: 5,
        communication: 4,
        value: 4
      }
    },
    {
      id: "3",
      user: {
        name: "Emma L.",
        avatar: "",
        memberSince: "2024",
        location: "Cork"
      },
      rating: 5,
      comment: "This is exactly what I needed! The sauna was hot enough to really sweat, and the cold plunge was perfectly refreshing. The changing area was clean and the whole experience felt very private and peaceful.",
      date: "2024-12-08",
      categories: {
        cleanliness: 5,
        heat_quality: 5,
        atmosphere: 5,
        arrival_experience: 5,
        privacy_comfort: 5,
        communication: 5,
        value: 5
      }
    }
  ];

  const allAmenities = [
    "Wood-fired sauna",
    "Cold plunge pool",
    "Outdoor seating & fire pit",
    "Changing area",
    "Free herbal tea",
    "Heated towels",
    "Private parking",
    "Atlantic sea views",
    "Shower facilities",
    "Towels provided",
    "Lockers available",
    "Outdoor deck",
    "Garden area",
    "BBQ facilities",
    "WiFi available",
    "Music system"
  ];

  return (
    <>
      <Helmet>
        <title>{location.name} | SaunaDip</title>
        <meta name="description" content={location.description} />
      </Helmet>
      
      {/* Amenities Modal */}
      {showAllAmenities && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-sauna-navy">All Amenities</h3>
                <Button
                  variant="ghost"
                  onClick={() => setShowAllAmenities(false)}
                  className="text-gray-500 hover:text-sauna-orange"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {allAmenities.map((amenity) => (
                  <div key={amenity} className="flex items-center space-x-2">
                    <div className="w-5 h-5 rounded-full bg-sauna-orange/10 flex items-center justify-center">
                      <CheckCircle className="h-3 w-3 text-sauna-orange" />
                    </div>
                    <span className="text-sauna-navy text-sm">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Form Modal */}
      {showReportForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-sauna-navy">Report This Listing</h3>
                <Button
                  variant="ghost"
                  onClick={() => setShowReportForm(false)}
                  className="text-gray-500 hover:text-sauna-orange"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-sauna-navy mb-2">
                    Reason for Report
                  </label>
                  <select className="w-full border border-gray-200 rounded-lg p-3 focus:border-sauna-orange focus:ring-sauna-orange/20">
                    <option>Select a reason...</option>
                    <option>Inaccurate information</option>
                    <option>Closed or no longer operating</option>
                    <option>Inappropriate content</option>
                    <option>Spam or fake listing</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-sauna-navy mb-2">
                    Additional Details
                  </label>
                  <textarea 
                    className="w-full border border-gray-200 rounded-lg p-3 min-h-[100px] focus:border-sauna-orange focus:ring-sauna-orange/20"
                    placeholder="Please provide more details..."
                  />
                </div>
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowReportForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-sauna-orange hover:bg-sauna-orange/90"
                    onClick={() => {
                      // TODO: Submit report
                      setShowReportForm(false);
                    }}
                  >
                    Submit Report
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Claim Form Modal */}
      {showClaimForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-sauna-navy">Claim This Sauna</h3>
                <Button
                  variant="ghost"
                  onClick={() => setShowClaimForm(false)}
                  className="text-gray-500 hover:text-sauna-orange"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Are you the owner or operator of this sauna? Claim it to manage your listing and respond to reviews.
                </p>
                <div>
                  <label className="block text-sm font-medium text-sauna-navy mb-2">
                    Your Name
                  </label>
                  <input 
                    type="text"
                    className="w-full border border-gray-200 rounded-lg p-3 focus:border-sauna-orange focus:ring-sauna-orange/20"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-sauna-navy mb-2">
                    Email Address
                  </label>
                  <input 
                    type="email"
                    className="w-full border border-gray-200 rounded-lg p-3 focus:border-sauna-orange focus:ring-sauna-orange/20"
                    placeholder="Enter your email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-sauna-navy mb-2">
                    Phone Number
                  </label>
                  <input 
                    type="tel"
                    className="w-full border border-gray-200 rounded-lg p-3 focus:border-sauna-orange focus:ring-sauna-orange/20"
                    placeholder="Enter your phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-sauna-navy mb-2">
                    Relationship to Sauna
                  </label>
                  <select className="w-full border border-gray-200 rounded-lg p-3 focus:border-sauna-orange focus:ring-sauna-orange/20">
                    <option>Select your role...</option>
                    <option>Owner</option>
                    <option>Manager</option>
                    <option>Operator</option>
                    <option>Employee</option>
                    <option>Other</option>
                  </select>
                </div>
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowClaimForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-sauna-orange hover:bg-sauna-orange/90"
                    onClick={() => {
                      // TODO: Submit claim
                      setShowClaimForm(false);
                    }}
                  >
                    Submit Claim
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Header Section */}
              <div className="space-y-4">
                {/* Breadcrumb */}
                <div className="flex items-center text-sm text-gray-600">
                  <Link to="/sauna-directory">
                    <span className="hover:text-sauna-orange cursor-pointer">T</span>
                  </Link>
                  <span className="mx-2">&gt;</span>
                  <span className="text-sauna-navy">{location.name}</span>
                </div>
                
                {/* Title and Rating */}
                <div className="space-y-2">
                  <h1 className="text-2xl sm:text-3xl font-bold text-sauna-navy">{location.name}</h1>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                      {renderStars(location.rating || 0)}
                      <span className="text-sauna-navy ml-1">
                        {location.rating || 0} ({Math.floor(Math.random() * 200) + 50} reviews)
                      </span>
                    </div>
                  </div>
                  <p className="text-sauna-navy">{location.address}</p>
                </div>
                
                {/* Description */}
                <p className="text-sauna-navy leading-relaxed">{location.description}</p>
                
                {/* Opening Days and Times */}
                <div className="bg-sauna-orange/5 border border-sauna-orange/20 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Clock className="h-4 w-4 text-sauna-orange" />
                    <h3 className="font-medium text-sauna-navy">Opening Hours</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-sauna-navy">Monday - Friday</span>
                      <span className="text-gray-600">9:00 AM - 8:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sauna-navy">Saturday</span>
                      <span className="text-gray-600">10:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sauna-navy">Sunday</span>
                      <span className="text-gray-600">10:00 AM - 4:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sauna-navy">Public Holidays</span>
                      <span className="text-gray-600">10:00 AM - 4:00 PM</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">* Sessions must be booked in advance</p>
                </div>
                
                {/* Action Buttons */}
                <div className="flex items-center space-x-4">
                  <Button 
                    variant="outline" 
                    className="border-sauna-orange text-sauna-navy hover:bg-sauna-orange hover:text-white"
                    onClick={() => setIsFavourited(!isFavourited)}
                  >
                    <Heart className={`h-4 w-4 mr-2 ${isFavourited ? 'fill-sauna-orange text-sauna-orange' : ''}`} />
                    Save
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-sauna-orange text-sauna-navy hover:bg-sauna-orange hover:text-white"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-sauna-orange text-sauna-navy hover:bg-sauna-orange hover:text-white"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Log Session
                  </Button>
                </div>
              </div>

              {/* Photo Gallery with Placeholders */}
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-2 h-96">
                  <div className="col-span-2 row-span-2">
                    <PhotoPlaceholder 
                      title={location.name}
                      subtitle="Main View"
                      size="lg"
                      className="w-full h-full"
                    />
                  </div>
                  <div className="col-span-1">
                    <PhotoPlaceholder 
                      title="Sauna Interior"
                      subtitle="Inside View"
                      size="md"
                      className="w-full h-full"
                    />
                  </div>
                  <div className="col-span-1">
                    <PhotoPlaceholder 
                      title="Sauna Facilities"
                      subtitle="Area"
                      size="md"
                      className="w-full h-full"
                    />
                  </div>
                  <div className="col-span-1">
                    <PhotoPlaceholder 
                      title="Sauna Amenities"
                      subtitle="Equipment"
                      size="md"
                      className="w-full h-full"
                    />
                  </div>
                  <div className="col-span-1 relative">
                    <PhotoPlaceholder 
                      title="Sauna Relaxation"
                      subtitle="Welcome"
                      size="md"
                      className="w-full h-full"
                    />
                    <button className="absolute bottom-2 right-2 bg-white text-sauna-navy px-3 py-1 rounded-lg text-sm font-medium border border-gray-200 hover:bg-sauna-orange hover:text-white transition-colors">
                      Show all photos
                    </button>
                  </div>
                </div>
              </div>

              {/* Sauna Summary - 4 Box Layout */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-sauna-navy">Sauna hosted by {location.operator?.name || "SaunaDip Team"}</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="border-gray-200">
                    <CardContent className="p-4 text-center">
                      <ThermometerSun className="h-6 w-6 mx-auto mb-2 text-sauna-orange" />
                      <p className="text-sm font-medium text-sauna-navy">Finnish</p>
                      <p className="text-xs text-gray-600">Sauna Type</p>
                    </CardContent>
                  </Card>
                  <Card className="border-gray-200">
                    <CardContent className="p-4 text-center">
                      <Flame className="h-6 w-6 mx-auto mb-2 text-sauna-orange" />
                      <p className="text-sm font-medium text-sauna-navy">Wood-fired</p>
                      <p className="text-xs text-gray-600">Heat Source</p>
                    </CardContent>
                  </Card>
                  <Card className="border-gray-200">
                    <CardContent className="p-4 text-center">
                      <Users className="h-6 w-6 mx-auto mb-2 text-sauna-orange" />
                      <p className="text-sm font-medium text-sauna-navy">6 guests</p>
                      <p className="text-xs text-gray-600">Capacity</p>
                    </CardContent>
                  </Card>
                  <Card className="border-gray-200">
                    <CardContent className="p-4 text-center">
                      <Clock className="h-6 w-6 mx-auto mb-2 text-sauna-orange" />
                      <p className="text-sm font-medium text-sauna-navy">90 min</p>
                      <p className="text-xs text-gray-600">Session</p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Sauna Details & Context */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-sauna-navy">Sauna Details & Context</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Sauna Details */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sauna-navy">Sauna Details</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Sauna Type:</span>
                        <span className="text-sm font-medium text-sauna-navy">Traditional Finnish</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Heat Source:</span>
                        <span className="text-sm font-medium text-sauna-navy">Wood-fired</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Operator Type:</span>
                        <span className="text-sm font-medium text-sauna-navy">Independent Owner</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Languages:</span>
                        <span className="text-sm font-medium text-sauna-navy">English, Irish</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Context & Features */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sauna-navy">Context & Features</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Location Context:</span>
                        <span className="text-sm font-medium text-sauna-navy">Atlantic Coast</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Access Type:</span>
                        <span className="text-sm font-medium text-sauna-navy">Private Experience</span>
                      </div>
                      <div className="space-y-2">
                        <span className="text-sm text-gray-600">Venue Features:</span>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="text-xs border-sauna-orange text-sauna-orange">
                            Firepit / Chill Zone
                          </Badge>
                          <Badge variant="outline" className="text-xs border-sauna-orange text-sauna-orange">
                            Relaxation Area
                          </Badge>
                          <Badge variant="outline" className="text-xs border-sauna-orange text-sauna-orange">
                            Shower / Wash Area
                          </Badge>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <span className="text-sm text-gray-600">Experience Add-ons:</span>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="text-xs border-sauna-orange text-sauna-orange">
                            Cold Plunge
                          </Badge>
                          <Badge variant="outline" className="text-xs border-sauna-orange text-sauna-orange">
                            Aromatherapy
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business & Contact Info */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-sauna-navy">Business & Contact Info</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Contact Details */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sauna-navy">Contact Details</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Email:</span>
                        <span className="text-sm font-medium text-sauna-navy">hello@saunadip.com</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Phone:</span>
                        <span className="text-sm font-medium text-sauna-navy">+353 87 123 4567</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Website:</span>
                        <span className="text-sm font-medium text-sauna-navy">saunadip.com</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Instagram:</span>
                        <span className="text-sm font-medium text-sauna-navy">@saunadip</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Location Details */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sauna-navy">Location Details</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Country:</span>
                        <span className="text-sm font-medium text-sauna-navy">Ireland</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">County:</span>
                        <span className="text-sm font-medium text-sauna-navy">Clare</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">City:</span>
                        <span className="text-sm font-medium text-sauna-navy">Ennis</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">State:</span>
                        <span className="text-sm font-medium text-sauna-navy">Clare</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* What this place offers */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-sauna-navy">What this place offers</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <Wifi className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">WiFi</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Lock className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Lockers</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Car className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Free parking</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Droplet className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Shower facilities</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Coffee className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Tea & coffee</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Accessibility className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Wheelchair accessible</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Towels provided</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Heated towels</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Free herbal tea</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Outdoor seating</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Fire pit</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Atlantic sea views</span>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  className="text-sauna-orange p-0 h-auto hover:text-sauna-orange-dark"
                  onClick={() => setShowAllAmenities(true)}
                >
                  Show all amenities
                </Button>
              </div>

              {/* Temperature Input Section */}
              <TemperatureInput
                locationId={location.id}
                locationName={location.name}
                onTemperatureSubmit={(data) => {
                  console.log('Temperature submitted:', data);
                  // TODO: Handle temperature submission
                }}
              />

              {/* Parking & Accessibility */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-sauna-navy">Parking & Accessibility</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Parking Details */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sauna-navy">Parking Information</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Parking Type:</span>
                        <span className="text-sm font-medium text-sauna-navy">Private Parking</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Number of spaces:</span>
                        <span className="text-sm font-medium text-sauna-navy">5</span>
                      </div>
                      <div className="space-y-2">
                        <span className="text-sm text-gray-600">Parking Notes:</span>
                        <p className="text-sm text-sauna-navy">Free parking available on premises. Gravel surface, suitable for all vehicles.</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Accessibility Features */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sauna-navy">Accessibility Features</h3>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-sauna-orange" />
                        <span className="text-sauna-navy text-sm">Ramp Access</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-sauna-orange" />
                        <span className="text-sauna-navy text-sm">Accessible Toilet</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-sauna-orange" />
                        <span className="text-sauna-navy text-sm">Wheelchair Accessible</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-sauna-orange" />
                        <span className="text-sauna-navy text-sm">Assistance Available</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Booking Information */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-sauna-navy">Booking Information</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Pricing & Sessions */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sauna-navy">Pricing & Sessions</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Base Price per Session:</span>
                        <span className="text-sm font-medium text-sauna-navy">€20</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Session Duration:</span>
                        <span className="text-sm font-medium text-sauna-navy">90 minutes</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Maximum Guests:</span>
                        <span className="text-sm font-medium text-sauna-navy">6 people</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Buffer Time Between Sessions:</span>
                        <span className="text-sm font-medium text-sauna-navy">15 minutes</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Availability Settings */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sauna-navy">Availability Settings</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Instant Booking:</span>
                        <span className="text-sm font-medium text-sauna-green">✓ Enabled</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Minimum Booking Notice:</span>
                        <span className="text-sm font-medium text-sauna-navy">1 hour(s)</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Advance Booking Limit:</span>
                        <span className="text-sm font-medium text-sauna-navy">30 days</span>
                      </div>
                      <div className="space-y-2">
                        <span className="text-sm text-gray-600">Booking & Payment Options:</span>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="text-xs border-sauna-orange text-sauna-orange">
                            Online Booking
                          </Badge>
                          <Badge variant="outline" className="text-xs border-sauna-orange text-sauna-orange">
                            Advance Payment Required
                          </Badge>
                          <Badge variant="outline" className="text-xs border-sauna-orange text-sauna-orange">
                            Cash Payment
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Additional Information Badges */}
                <div className="flex flex-wrap gap-4 mt-4">
                  <div className="flex items-center space-x-2 bg-sauna-orange/10 px-3 py-2 rounded-lg">
                    <Clock className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sm font-medium text-sauna-navy">90 mins / session</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-sauna-orange/10 px-3 py-2 rounded-lg">
                    <Users className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sm font-medium text-sauna-navy">Up to 6 guests</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-sauna-orange/10 px-3 py-2 rounded-lg">
                    <Award className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sm font-medium text-sauna-navy">Unlimited for now</span>
                  </div>
                </div>
                
                {/* Special Rules / Notes */}
                <div className="space-y-2">
                  <h3 className="font-medium text-sauna-navy">Special Rules / Notes</h3>
                  <p className="text-sm text-sauna-navy">
                    Sessions must be booked in advance. Please arrive 10 minutes before your scheduled time. 
                    Bring your own swimwear and towel. No glass containers in the sauna area. 
                    Weather-dependent sessions may be rescheduled with 24-hour notice.
                  </p>
                </div>
              </div>

              {/* Services available */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-sauna-navy">Services available</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Finnish sauna sessions</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Cold plunge therapy</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Wellness consultation</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Steam room access</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-sauna-orange" />
                    <span className="text-sauna-navy text-sm">Massage therapy</span>
                  </div>
                </div>
              </div>

              {/* Special experience features */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-sauna-navy">Special experience features</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card className="border-gray-200">
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-3">
                        <Heart className="h-5 w-5 text-sauna-orange mt-0.5" />
                        <div>
                          <h3 className="font-medium text-sauna-navy">Cold plunge pool</h3>
                          <p className="text-sm text-gray-600">Invigorating ice bath experience</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-gray-200">
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-3">
                        <Heart className="h-5 w-5 text-sauna-orange mt-0.5" />
                        <div>
                          <h3 className="font-medium text-sauna-navy">Aromatherapy</h3>
                          <p className="text-sm text-gray-600">Essential oils and scents</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Where you'll be */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-sauna-navy">Where you'll be</h2>
                <div className="space-y-4">
                  {/* Map Placeholder */}
                  <div className="w-full h-64 bg-gradient-to-br from-sauna-orange/10 to-sauna-blue/10 border border-sauna-orange/20 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <Map className="h-12 w-12 text-sauna-orange mx-auto mb-2" />
                      <p className="text-sauna-navy font-medium">Map of Helsinki</p>
                      <p className="text-sm text-gray-600">Location marked with blue pin</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-sauna-navy">{location.address}</p>
                    <Button 
                      variant="outline" 
                      className="border-sauna-orange text-sauna-navy hover:bg-sauna-orange hover:text-white"
                      onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(location.address)}`, '_blank')}
                    >
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Directions
                    </Button>
                  </div>
                </div>
              </div>

              {/* Things to know */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-sauna-navy">Things to know</h2>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                  <div className="space-y-3">
                    <h3 className="font-medium text-sauna-navy">House rules</h3>
                    <ul className="space-y-2">
                      <li className="text-sauna-navy text-sm flex items-start space-x-2">
                        <span className="text-sauna-orange mt-1">•</span>
                        <span>No children under 10</span>
                      </li>
                      <li className="text-sauna-navy text-sm flex items-start space-x-2">
                        <span className="text-sauna-orange mt-1">•</span>
                        <span>Bring your own swimwear</span>
                      </li>
                      <li className="text-sauna-navy text-sm flex items-start space-x-2">
                        <span className="text-sauna-orange mt-1">•</span>
                        <span>No food or drinks in sauna</span>
                      </li>
                      <li className="text-sauna-navy text-sm flex items-start space-x-2">
                        <span className="text-sauna-orange mt-1">•</span>
                        <span>Minimum 2-hour sessions</span>
                      </li>
                    </ul>
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-medium text-sauna-navy">Safety & property</h3>
                    <ul className="space-y-2">
                      <li className="text-sauna-navy text-sm flex items-start space-x-2">
                        <span className="text-sauna-orange mt-1">•</span>
                        <span>Security cameras on property</span>
                      </li>
                      <li className="text-sauna-navy text-sm flex items-start space-x-2">
                        <span className="text-sauna-orange mt-1">•</span>
                        <span>First aid available</span>
                      </li>
                      <li className="text-sauna-navy text-sm flex items-start space-x-2">
                        <span className="text-sauna-orange mt-1">•</span>
                        <span>Emergency contact provided</span>
                      </li>
                      <li className="text-sauna-navy text-sm flex items-start space-x-2">
                        <span className="text-sauna-orange mt-1">•</span>
                        <span>Professional supervision</span>
                      </li>
                    </ul>
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-medium text-sauna-navy">Cancellation policy</h3>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-sauna-orange" />
                        <span className="text-sauna-navy text-sm">Free cancellation 24h before</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-sauna-orange" />
                        <span className="text-sauna-navy text-sm">50% charge for late cancellation</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="h-4 w-4 text-sauna-orange" />
                        <span className="text-sauna-navy text-sm">No refund for no-shows</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-sauna-orange" />
                        <span className="text-sauna-navy text-sm">Rescheduling allowed once</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reviews Section */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-sauna-navy">Reviews</h2>
                <ReviewsSection
                  averageRating={4.7}
                  totalReviews={3}
                  reviews={sampleReviews}
                  categories={[
                    {
                      name: "Cleanliness",
                      icon: <Sparkles className="h-4 w-4 text-sauna-orange" />,
                      rating: 4.9,
                      total: 3
                    },
                    {
                      name: "Heat & Chill Quality",
                      icon: <ThermometerSun className="h-4 w-4 text-sauna-orange" />,
                      rating: 4.8,
                      total: 3
                    },
                    {
                      name: "Atmosphere",
                      icon: <Heart className="h-4 w-4 text-sauna-orange" />,
                      rating: 5.0,
                      total: 3
                    },
                    {
                      name: "Arrival Experience",
                      icon: <Key className="h-4 w-4 text-sauna-orange" />,
                      rating: 4.3,
                      total: 3
                    },
                    {
                      name: "Privacy & Comfort",
                      icon: <Shield className="h-4 w-4 text-sauna-orange" />,
                      rating: 4.7,
                      total: 3
                    },
                    {
                      name: "Communication",
                      icon: <MessageSquare className="h-4 w-4 text-sauna-orange" />,
                      rating: 4.8,
                      total: 3
                    },
                    {
                      name: "Value for Money",
                      icon: <Tag className="h-4 w-4 text-sauna-orange" />,
                      rating: 4.6,
                      total: 3
                    }
                  ]}
                  onWriteReview={() => console.log('Write review clicked')}
                  locationId={location.id}
                  locationName={location.name}
                />
              </div>
            </div>

            {/* Right Column - Operator Contact */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                {/* Operator Contact Card */}
                <Card className="border-gray-200 shadow-lg bg-white">
                  <CardContent className="p-6 space-y-4">
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold text-sauna-navy">Ready to experience this sauna?</h3>
                      <p className="text-sm text-gray-600">This sauna takes bookings through their own site.</p>
                    </div>
                    
                    {/* Primary Booking CTA */}
                    <Button 
                      className="w-full bg-sauna-orange hover:bg-sauna-orange/90 text-white font-semibold"
                      onClick={() => window.open(location.operator?.website || 'https://saunadip.com', '_blank')}
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Book via Operator Website
                    </Button>
                    
                    {/* Secondary Contact Options */}
                    <div className="space-y-3">
                      <Button 
                        variant="outline" 
                        className="w-full border-sauna-orange text-sauna-navy hover:bg-sauna-orange hover:text-white transition-colors"
                        onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(location.address)}`, '_blank')}
                      >
                        <MapPin className="h-4 w-4 mr-2" />
                        Get Directions
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        className="w-full border-sauna-orange text-sauna-navy hover:bg-sauna-orange hover:text-white transition-colors"
                        onClick={() => {
                          const phone = location.operator?.phone;
                          if (phone) {
                            const message = encodeURIComponent("Hi, I found you via SaunaDip and would like to book a session. Could you tell me more about availability?");
                            window.open(`https://wa.me/${phone.replace(/\D/g, '')}?text=${message}`, '_blank');
                          }
                        }}
                      >
                        <Phone className="h-4 w-4 mr-2" />
                        Message on WhatsApp
                      </Button>
                    </div>
                    
                    {/* Engagement CTAs */}
                    <div className="pt-4 border-t border-gray-200">
                      <div className="space-y-2">
                        <Button 
                          variant="ghost" 
                          className="w-full text-sauna-navy hover:bg-sauna-orange/10 hover:text-sauna-orange transition-colors"
                          onClick={() => {
                            setIsFavourited(!isFavourited);
                            // TODO: Implement save to favourites
                            console.log('Save to favourites clicked');
                          }}
                        >
                          <Heart className={`h-4 w-4 mr-2 ${isFavourited ? 'fill-sauna-orange text-sauna-orange' : ''}`} />
                          {isFavourited ? 'Saved to Favourites' : 'Save to Favourites'}
                        </Button>
                        
                        <Button 
                          variant="ghost" 
                          className="w-full text-sauna-navy hover:bg-sauna-orange/10 hover:text-sauna-orange transition-colors"
                          onClick={() => {
                            // TODO: Implement log dip functionality
                            console.log('Log this dip clicked');
                          }}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Log This Dip
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Button 
                        variant="ghost" 
                        className="w-full text-sauna-navy hover:bg-sauna-orange/10 hover:text-sauna-orange transition-colors"
                        onClick={() => setShowClaimForm(true)}
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        Claim This Sauna
                      </Button>
                      
                      <Button 
                        variant="ghost" 
                        className="w-full text-sauna-navy p-0 h-auto text-sm underline hover:text-sauna-orange"
                        onClick={() => setShowReportForm(true)}
                      >
                        Report this listing
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SaunaDetailsNordic; 